import {BaseQueryApi} from '@reduxjs/toolkit/query/react';
import {addBreadcrumb, captureException} from '@sentry/browser';
import axios from 'axios';
import {AxiosCacheInstance, buildMemoryStorage, setupCache} from 'axios-cache-interceptor';
import {isFeatureEnabled} from 'feature-flags';

// eslint-disable-next-line no-restricted-imports
import {path} from 'ramda';

import {browserStorageKey} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import {getWorkspaceFromUri} from '@dms/routes';

import {applyWorkspaceHost} from './applyWorkspaceHost';
import {customRequestInterceptor} from './customRequestInterceptor';
import {getArgsWithAuthorization} from './getArgsWithAuthorization';
import {refreshAccessToken} from './refreshAccessToken';

const URL_PATH_PREFIX = 'dms';

// Retry configuration
const MAX_RETRY_COUNT = 1;
const RETRY_DELAY_MS = 3000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];

// Track if axios has been initialized
let isAxiosInitialized = false;

export const initializeAxios = (api: BaseQueryApi) => {
  // Only initialize axios once
  if (isAxiosInitialized) {
    return;
  }

  isAxiosInitialized = true;
  axios.defaults.baseURL = `${process.env.API_URL}/${URL_PATH_PREFIX}`;
  axios.interceptors.request.use((config) => {
    const activeBranchId = path(['branch', 'activeBranchId'], api.getState());
    // Applied per request; the helper's idempotency guard keeps internal retries safe
    config.baseURL = applyWorkspaceHost(config.baseURL, getWorkspaceFromUri().workspace);
    // @ts-ignore
    config.headers = getArgsWithAuthorization({headers: config.headers}, activeBranchId).headers;

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const isRetryEnabled = isFeatureEnabled(featureFlags.RETRY_INTERCEPTOR);
      const responseStatus = Number(error.response?.status) || Number(error.status);

      if (responseStatus >= 500) {
        if (isRetryEnabled) {
          // Check if this request has already been retried
          const retryCount = error.config?.retryCount || 0;

          const isInfrastructureError = INFRASTRUCTURE_ERRORS_CODES.includes(responseStatus);

          if (error.response === undefined || !isInfrastructureError) {
            captureException(error);
          }

          if (isInfrastructureError && retryCount === MAX_RETRY_COUNT) {
            captureException(error);
          }

          // If server error and we haven't exceeded max retries
          if (isInfrastructureError && retryCount < MAX_RETRY_COUNT) {
            addBreadcrumb({
              category: 'api',
              message: `Retrying datagrid API request ${error?.config?.url}, because response status is ${responseStatus}`,
              level: 'error',
            });

            // Increment retry count
            const updatedConfig = {
              ...error.config,
              retryCount: retryCount + 1,
              cache: false,
            };

            // Return a promise that resolves after delay
            return new Promise((resolve) => {
              setTimeout(
                () => {
                  resolve(axios.request(updatedConfig));
                },
                RETRY_DELAY_MS * (retryCount + 1)
              ); // Exponential backoff
            });
          }
        } else {
          captureException(error);
        }
      }

      if (responseStatus === 401) {
        const refreshed = await refreshAccessToken(api.dispatch, api.getState);

        if (!refreshed) {
          // refreshAccessToken has already redirected to login/workspace.
          return error;
        }

        const token = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);

        return axios.request({
          ...error.config,
          cache: false,
          headers: {...error.config.headers, Authorization: `Bearer ${token}`},
        });
      }

      throw error;
    }
  );

  // eslint-disable-next-line eslint-js/no-restricted-syntax
  if (!(axios as unknown as AxiosCacheInstance).storage) {
    setupCache(axios, {
      storage: buildMemoryStorage(false, false, false),
      // eslint-disable-next-line eslint-js/no-restricted-syntax
      requestInterceptor: customRequestInterceptor(axios as unknown as AxiosCacheInstance),
      ttl: 750,
      cacheTakeover: false,
    });
  }
};
